// src/pages/Blacklist.js
// Fraud Management → Blacklist Management page.
// Assembles: Sidebar + Navbar + Summary Cards + Filter Bar + Table + Drawers + Dialogs.

import React from 'react';
import { Container } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import DashboardNavbar from '../components/DashboardNavbar';

import useBlacklist from '../hooks/useBlacklist';
import BlacklistSummaryCards from '../components/fraud/BlacklistSummaryCards';
import BlacklistFilterBar from '../components/fraud/BlacklistFilterBar';
import BlacklistTable from '../components/fraud/BlacklistTable';
import BlacklistViewDrawer from '../components/fraud/BlacklistViewDrawer';
import BlacklistEntryDialog from '../components/fraud/BlacklistEntryDialog';
import BlacklistDeleteConfirm from '../components/fraud/BlacklistDeleteConfirm';

// ─── In-page toast notification ───────────────────────────────────────────────
// Self-contained; no external library required.
const Toast = ({ toast }) => {
  if (!toast) return null;
  const isError = toast.type === 'error';
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 28,
        right: 28,
        zIndex: 2000,
        background: isError ? 'rgba(239,68,68,0.92)' : 'rgba(34,197,94,0.92)',
        backdropFilter: 'blur(8px)',
        color: '#fff',
        borderRadius: 10,
        padding: '12px 20px',
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        maxWidth: 360,
        animation: 'blToastIn 0.25s ease',
      }}
    >
      <span>{isError ? '✗' : '✓'}</span>
      <span>{toast.message}</span>
      <style>{`@keyframes blToastIn { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
};

// ─── Error banner ────────────────────────────────────────────────────────────
const ErrorBanner = ({ message }) => (
  <div
    style={{
      background: 'rgba(239,68,68,0.10)',
      border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8,
      padding: '10px 16px',
      marginBottom: 16,
      fontSize: 12,
      color: '#EF4444',
    }}
  >
    ⚠️ {message}
  </div>
);

// ─── Page ────────────────────────────────────────────────────────────────────
const Blacklist = () => {
  const bl = useBlacklist();

  const hasFilters = bl.search.trim() !== '' || bl.statusFilter !== 'all' || bl.typeFilter !== 'all';

  return (
    <div className="d-flex entire-bg-color">
      <Sidebar />

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <DashboardNavbar />

        {/* ── Summary Cards ── */}
        <BlacklistSummaryCards stats={bl.stats} loading={bl.loading} />

        {/* ── Body ── */}
        <section className="py-4 nav-body-bg btm-rad">
          <Container fluid className="px-4">

            {/* Page heading */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h3 className="text-h2 mb-0">Blacklist</h3>
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--text-secondary)',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--divider)',
                  borderRadius: 20,
                  padding: '3px 12px',
                }}
              >
                {bl.loading ? 'Loading…' : `${bl.filtered.length} ${bl.filtered.length === 1 ? 'entry' : 'entries'} shown`}
              </span>
            </div>

            {/* Error notice */}
            {bl.error && <ErrorBanner message={bl.error} />}

            {/* Filter bar */}
            <BlacklistFilterBar
              search={bl.search}
              onSearchChange={bl.setSearch}
              statusFilter={bl.statusFilter}
              onStatusChange={bl.setStatusFilter}
              typeFilter={bl.typeFilter}
              onTypeChange={bl.setTypeFilter}
              onAdd={bl.openAdd}
              onRefresh={bl.fetchAll}
              loading={bl.loading}
            />

            {/* Table */}
            <BlacklistTable
              entries={bl.pageEntries}
              loading={bl.loading}
              opLoading={bl.opLoading}
              hasFilters={hasFilters}
              page={bl.page}
              totalPages={bl.totalPages}
              hasNext={bl.hasNext}
              hasPrev={bl.hasPrev}
              onNext={() => bl.setPage((p) => p + 1)}
              onPrev={() => bl.setPage((p) => p - 1)}
              total={bl.filtered.length}
              onView={bl.openView}
              onEdit={bl.openEdit}
              onToggle={bl.toggleActive}
              onDelete={bl.openDeleteConfirm}
            />

          </Container>
        </section>

        <div style={{ height: 40 }} />
      </div>

      {/* ── View drawer ── */}
      <BlacklistViewDrawer
        entry={bl.viewEntry}
        open={bl.viewOpen}
        onClose={() => bl.setViewOpen(false)}
        onEdit={bl.openEdit}
        onToggle={bl.toggleActive}
        onDelete={bl.openDeleteConfirm}
      />

      {/* ── Add / Edit dialog ── */}
      <BlacklistEntryDialog
        open={bl.dialogOpen}
        editEntry={bl.editEntry}
        onClose={() => bl.setDialogOpen(false)}
        onSave={bl.editEntry ? bl.updateEntry : bl.addEntry}
        opLoading={bl.opLoading}
      />

      {/* ── Delete confirmation ── */}
      <BlacklistDeleteConfirm
        open={bl.deleteOpen}
        entry={bl.deleteTarget}
        onClose={() => bl.setDeleteOpen(false)}
        onConfirm={bl.deleteEntry}
        opLoading={bl.opLoading}
      />

      {/* ── Toast notifications ── */}
      <Toast toast={bl.toast} />
    </div>
  );
};

export default Blacklist;
